/** Live data queries via existing portal APIs (Phase 2). */

import api from "services/api";
import { fetchTeacherList } from "services/teacherListService";
import {
  fetchTeacherLicenseSummary,
  fetchTeacherLicenseList,
} from "services/teacherLicenseService";
import { apiurl } from "utils";
import { format, subDays } from "date-fns";
import {
  parseListPayload,
  extractLeaveListStats,
  extractAssignmentSummary,
  extractTeacherListStats,
  extractEstablishmentListStats,
} from "utils/paginationStats";
import { normalizeReg, normalizeTeacherText, parseRegistrationHint } from "assistant/teacherQuery";
import { parseDashboardTeacherQuota } from "layouts/dashboard/dashboardChartHelpers";
import { resolveProfilePic } from "utils/teacherAvatar";
import { readIsLicensed } from "utils/teacherFlags";

const MOBILE_API_ROOT = "https://api.everifpro.com/api/mobile";
const withSlash = (url = "") => (url.endsWith("/") ? url : `${url}/`);
const leaveApiBase = withSlash(process.env.REACT_APP_LEAVE_API_BASE || apiurl);

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normalizeSearch(text) {
  return normalizeTeacherText(text);
}

function regEquals(row, hint) {
  const reg = normalizeReg(row.regNumber ?? row.RegNumber ?? row.registrationNo ?? row.RegistrationNo ?? "");
  const target = normalizeReg(hint);
  if (!reg || !target) return false;
  return reg === target;
}

export function mapTeacherRow(row) {
  const name = [row.name ?? row.Name, row.preName ?? row.PreName].filter(Boolean).join(" ");
  return {
    id: row.id ?? row.Id,
    name: name || (row.fullName ?? row.FullName) || "—",
    reg: row.regNumber ?? row.RegNumber ?? row.registrationNo ?? row.RegistrationNo ?? "—",
    phone: row.phone ?? row.Phone ?? row.phoneNumber ?? row.PhoneNumber ?? "—",
    status: row.status ?? row.Status ?? "",
    recruiter: row.recruiter ?? row.Recruiter ?? "",
    profilePic: resolveProfilePic(row),
    hasProfilePic: row.hasProfilePic ?? row.HasProfilePic,
    score: row.score ?? row.Score ?? null,
    degree: row.degree ?? row.Degree ?? "",
    isLicensed: readIsLicensed(row.isLicensed ?? row.IsLicensed),
  };
}

function teacherMatchesQuery(row, query) {
  const q = normalizeSearch(query);
  if (!q) return true;

  const reg = normalizeReg(row.regNumber ?? row.RegNumber ?? row.registrationNo ?? row.RegistrationNo ?? "");
  if (/^\d{3,}$/.test(q.replace(/[\s.-]/g, ""))) {
    return reg.includes(q.replace(/[\s.-]/g, "")) || reg === q.replace(/[\s.-]/g, "");
  }

  const haystack = [
    row.name,
    row.preName,
    row.Name,
    row.PreName,
    row.regNumber,
    row.RegNumber,
    row.phone,
    row.Phone,
  ]
    .filter(Boolean)
    .map(normalizeSearch)
    .join(" ");
  return haystack.includes(q);
}

async function fetchTeacherRows({ allCategories = false } = {}) {
  const params = { pageNumber: 1, pageSize: 5000 };
  if (!allCategories) params.category = 1;
  const { data } = await api.get("/Teacher/list", { params, headers: authHeaders() });
  return parseListPayload(data);
}

/** Resolve a teacher by name, matricule, or id hint — used for story/search. */
export async function resolveTeacherQuery(query, limit = 8) {
  const regHint = parseRegistrationHint(query);
  const trimmed = String(query ?? "").trim();
  const numericOnly = /^\d{3,}[A-Za-z0-9-]*$/.test(trimmed);
  const searchHint = regHint || trimmed;
  const allCategories = Boolean(regHint || numericOnly);

  const rows = await fetchTeacherRows({ allCategories });
  const mapped = (list) => list.map(mapTeacherRow);

  if (regHint || numericOnly) {
    const target = regHint || trimmed;
    const exact = rows.filter((row) => regEquals(row, target));
    if (exact.length === 1) {
      return { status: "found", teacher: mapTeacherRow(exact[0]), query: target };
    }
    if (exact.length > 1) {
      return { status: "ambiguous", matches: mapped(exact).slice(0, limit), query: target };
    }
    const partial = rows.filter((row) => {
      const reg = normalizeReg(row.regNumber ?? row.RegNumber ?? "");
      const t = normalizeReg(target);
      return reg && t && reg.includes(t);
    });
    if (partial.length === 1) {
      return { status: "found", teacher: mapTeacherRow(partial[0]), query: target };
    }
    if (partial.length > 1) {
      return { status: "ambiguous", matches: mapped(partial).slice(0, limit), query: target };
    }
    return { status: "not_found", query: target };
  }

  const filtered = rows.filter((row) => teacherMatchesQuery(row, searchHint));
  if (!filtered.length) return { status: "not_found", query: searchHint };
  if (filtered.length === 1) return { status: "found", teacher: mapTeacherRow(filtered[0]), query: searchHint };

  const exactName = filtered.filter((row) => {
    const full = [row.name, row.preName].filter(Boolean).join(" ");
    const nFull = normalizeSearch(full);
    const nQ = normalizeSearch(searchHint);
    return nFull === nQ || nFull.includes(nQ) || nQ.includes(nFull);
  });
  if (exactName.length === 1) {
    return { status: "found", teacher: mapTeacherRow(exactName[0]), query: searchHint };
  }

  return {
    status: "ambiguous",
    matches: mapped(filtered).slice(0, limit),
    query: searchHint,
  };
}

export async function findTeacherById(teacherId) {
  if (!teacherId) return null;
  const rows = await fetchTeacherRows({ allCategories: true });
  const row = rows.find((r) => String(r.id ?? r.Id) === String(teacherId));
  return row ? mapTeacherRow(row) : null;
}

export async function searchTeachers(query, limit = 5, options = {}) {
  const resolved = await resolveTeacherQuery(query, Math.max(limit, 8));
  if (resolved.status === "found") {
    return {
      stats: { totalResults: 1 },
      items: [resolved.teacher],
      totalMatches: 1,
    };
  }
  if (resolved.status === "ambiguous") {
    return {
      stats: { totalResults: resolved.matches.length },
      items: resolved.matches.slice(0, limit),
      totalMatches: resolved.matches.length,
    };
  }
  return {
    stats: { totalResults: 0 },
    items: [],
    totalMatches: 0,
  };
}

export async function fetchTeacherStats() {
  const result = await fetchTeacherList({ pageNumber: 1, pageSize: 1, category: 1, filters: {} });
  return result.stats;
}

export async function fetchRecruiterCount() {
  const { data } = await api.get("Recruiters/list", { headers: authHeaders() });
  const list = Array.isArray(data) ? data : parseListPayload(data);
  return { count: list.length, items: list.slice(0, 5).map((r) => ({
    id: r.id ?? r.Id,
    name: r.name ?? r.Name ?? r.fullName ?? "—",
    phone: r.phoneNumber ?? r.PhoneNumber ?? "—",
  })) };
}

export async function fetchLeaveStats() {
  const { data } = await api.get(`${leaveApiBase}LeaveRequests/list`, {
    params: { pageNumber: 1, pageSize: 10 },
    headers: authHeaders(),
  });
  const rows = parseListPayload(data);
  const stats = extractLeaveListStats(rows);
  const pending = rows.filter((r) =>
    String(r.status ?? r.Status ?? "").toLowerCase() === "pending"
  );
  return { stats, pendingPreview: pending.slice(0, 3) };
}

export async function fetchAssignmentStats() {
  const { data } = await api.get("/Assignment/summary", {
    params: { pageNumber: 1, pageSize: 1 },
    headers: authHeaders(),
  });
  return extractAssignmentSummary(parseListPayload(data), data);
}

export async function fetchEstablishmentStats() {
  const token = localStorage.getItem("tk446655440000");
  const { data } = await api.get(`${MOBILE_API_ROOT}/establishments`, {
    headers: {
      ...authHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const rows = parseListPayload(data);
  return extractEstablishmentListStats(rows);
}

export async function fetchDashboardStats() {
  const end = new Date();
  const start = subDays(end, 30);
  const fmt = (d) => format(d, "yyyy-MM-dd");
  const { data } = await api.get(
    `Dashboard/stats?StartDate=${encodeURIComponent(fmt(start))}&EndDate=${encodeURIComponent(fmt(end))}`,
    { headers: authHeaders() }
  );
  return parseDashboardTeacherQuota(data ?? {});
}

export async function fetchAreaAttendanceSummary(options = {}) {
  const end = options.endDate || format(new Date(), "yyyy-MM-dd");
  const start = options.startDate || format(subDays(new Date(end), 30), "yyyy-MM-dd");
  const days = options.days ?? 1;

  const { data } = await api.get("/Teacher/areaattendancesummary", {
    params: {
      startDate: String(start).slice(0, 10),
      endDate: String(end).slice(0, 10),
      days,
    },
    headers: authHeaders(),
  });
  const rows = parseListPayload(data);
  const assigned = rows.reduce((s, r) => s + Number(r?.teachersAssigned ?? 0), 0);
  const checked = rows.reduce((s, r) => s + Number(r?.teachersChecked ?? 0), 0);
  const rate = assigned > 0 ? Math.round((checked / assigned) * 100) : 0;
  const provinceNames = new Set(rows.map((r) => r?.provinceName).filter(Boolean));

  return {
    startDate: start,
    endDate: end,
    days,
    rowCount: rows.length,
    provinces: provinceNames.size,
    departments: rows.length,
    teachersAssigned: assigned,
    teachersChecked: checked,
    checkRate: rate,
    preview: rows.slice(0, 5).map((r) => ({
      name: [r.provinceName, r.departmentName].filter(Boolean).join(" · ") || "—",
      reg: "",
      detail: `${r.teachersChecked ?? 0}/${r.teachersAssigned ?? 0} checked`,
    })),
    rows,
  };
}

export async function fetchEstablishmentAttendanceReport(options = {}) {
  const end = options.endDate || format(new Date(), "yyyy-MM-dd");
  const start = options.startDate || format(subDays(new Date(end), 30), "yyyy-MM-dd");
  const radius = options.radius ?? 500;

  const params = {
    startDate: String(start).slice(0, 10),
    endDate: String(end).slice(0, 10),
    radius: Number(radius),
    pageNumber: 1,
    pageSize: 5000,
  };
  if (options.estabId) params.estabId = options.estabId;

  const { data } = await api.get("/Teacher/establishmentattendancereport", {
    params,
    headers: authHeaders(),
  });
  const teachers = Array.isArray(data?.teachers) ? data.teachers : [];
  const withPresence = teachers.filter((t) => Number(t.totalDaysPresent ?? 0) > 0).length;
  const inRadius = teachers.filter((t) => Number(t.totalDaysPresentInRadius ?? 0) > 0).length;

  const sortedByPresence = [...teachers].sort(
    (a, b) => Number(a.totalDaysPresent ?? 0) - Number(b.totalDaysPresent ?? 0)
  );
  const lowAttendance = sortedByPresence.slice(0, 5).map((t) => ({
    name: t.teacherName ?? "—",
    reg: t.regNumber ?? "—",
    detail: `${t.totalDaysPresent ?? 0} days present · ${t.totalDaysPresentInRadius ?? 0} in radius`,
    id: t.teacherId ?? t.id ?? t.Id,
  }));

  return {
    startDate: start,
    endDate: end,
    radius,
    totalTeachers: teachers.length,
    withPresence,
    inRadius,
    preview: teachers.slice(0, 5).map((t) => ({
      name: t.teacherName ?? "—",
      reg: t.regNumber ?? "—",
      detail: `${t.totalDaysPresent ?? 0} days present · ${t.totalDaysPresentInRadius ?? 0} in radius`,
    })),
    lowAttendance,
    teachers,
  };
}

/** License quota + unlicensed preview for assistant. */
export async function fetchLicenseQuota() {
  const data = await fetchTeacherLicenseSummary();
  return {
    totalTeachers: Number(
      data?.totalPeople ?? data?.TotalPeople ?? data?.totalTeachers ?? data?.TotalTeachers ?? 0
    ),
    licensedCount: Number(
      data?.licensedPeople ?? data?.LicensedPeople ?? data?.licensedCount ?? data?.LicensedCount ?? 0
    ),
    unlicensedCount: Number(
      data?.pendingActivationPeople ??
        data?.PendingActivationPeople ??
        data?.unlicensedCount ??
        data?.UnlicensedCount ??
        0
    ),
    maxLicensedTeachers: Number(
      data?.maximumLicenses ??
        data?.MaximumLicenses ??
        data?.maxLicensedTeachers ??
        data?.MaxLicensedTeachers ??
        0
    ),
    remainingLicenses: Number(data?.remainingLicenses ?? data?.RemainingLicenses ?? 0),
  };
}

export async function fetchUnlicensedTeachers(limit = 5) {
  const data = await fetchTeacherLicenseList({
    pageNumber: 1,
    pageSize: Math.max(1, limit),
    isLicensed: false,
  });
  const rows = parseListPayload(data);
  return rows.slice(0, limit).map(mapTeacherRow);
}

/** Phase 3 — proactive insight bundle */
export async function fetchProactiveInsights() {
  const results = await Promise.allSettled([
    fetchLeaveStats(),
    fetchRecruiterCount(),
    fetchTeacherStats(),
    fetchAssignmentStats(),
    fetchDashboardStats(),
    fetchLicenseQuota(),
  ]);

  const unwrap = (i) => (results[i].status === "fulfilled" ? results[i].value : null);

  const leaves = unwrap(0);
  const recruiters = unwrap(1);
  const teachers = unwrap(2);
  const assignments = unwrap(3);
  const dashboard = unwrap(4);
  const licenses = unwrap(5);

  const insights = [];

  if (leaves?.stats?.pending > 0) {
    insights.push({
      id: "pending-leaves",
      severity: "warning",
      titleKey: "assistant.insight.pending_leaves",
      titleFallback: "{{count}} demande(s) de congé en attente",
      count: leaves.stats.pending,
      route: "/leave-requests",
      actionKey: "assistant.action.review_leaves",
      actionFallback: "Voir les congés",
    });
  }

  if (licenses?.remainingLicenses === 0 && licenses?.maxLicensedTeachers > 0 && licenses?.unlicensedCount > 0) {
    insights.push({
      id: "license-exhausted",
      severity: "warning",
      titleKey: "assistant.insight.license_exhausted",
      titleFallback: "Plus de créneaux licence — {{count}} enseignant(s) non licencié(s)",
      count: licenses.unlicensedCount,
      route: "/teacher-license",
      actionKey: "assistant.action.view_teacher_licenses",
      actionFallback: "Ouvrir licences",
    });
  } else if (licenses?.unlicensedCount > 0 && licenses?.remainingLicenses > 0) {
    insights.push({
      id: "license-slots",
      severity: "info",
      titleKey: "assistant.insight.license_slots",
      titleFallback: "{{count}} créneau(x) licence restant(s)",
      count: licenses.remainingLicenses,
      route: "/teacher-license",
      actionKey: "assistant.action.view_teacher_licenses",
      actionFallback: "Ouvrir licences",
    });
  } else if (
    dashboard?.remainingLicenses === 0 &&
    dashboard?.maxLicensedTeachers > 0 &&
    dashboard?.totalTeachers > dashboard?.licensedTeachers
  ) {
    insights.push({
      id: "license-exhausted",
      severity: "warning",
      titleKey: "assistant.insight.license_exhausted",
      titleFallback: "No license slots remaining — activate teachers from Teacher Licenses when slots are available",
      count: dashboard.totalTeachers - dashboard.licensedTeachers,
      route: "/teacher-license",
      actionKey: "assistant.action.view_teacher_licenses",
      actionFallback: "Open teacher licenses",
    });
  }

  if (teachers?.totalTeachers != null) {
    insights.push({
      id: "teacher-roster",
      severity: "info",
      titleKey: "assistant.insight.teacher_roster",
      titleFallback: "{{count}} enseignante(s) au total",
      count: teachers.totalTeachers,
      route: "/enseignantes",
      actionKey: "assistant.action.view_teachers",
      actionFallback: "Voir enseignantes",
    });
  }

  if (recruiters?.count != null) {
    insights.push({
      id: "recruiters",
      severity: "info",
      titleKey: "assistant.insight.recruiters",
      titleFallback: "{{count}} recruteur(s) actifs",
      count: recruiters.count,
      route: "/recruiters",
      actionKey: "assistant.action.view_recruiters",
      actionFallback: "Voir recruteurs",
    });
  }

  if (assignments?.totalRecords != null && assignments.totalRecords > 0) {
    insights.push({
      id: "assignments",
      severity: "info",
      titleKey: "assistant.insight.assignments",
      titleFallback: "{{count}} affectation(s) enregistrée(s)",
      count: assignments.totalRecords,
      route: "/affectations",
      actionKey: "assistant.action.view_assignments",
      actionFallback: "Voir affectations",
    });
  }

  return {
    insights,
    raw: { leaves, recruiters, teachers, assignments, dashboard, licenses },
  };
}

export async function fetchDashboardSummaryCards(language) {
  const [teachers, leaves, recruiters, assignments, dashboard] = await Promise.allSettled([
    fetchTeacherStats(),
    fetchLeaveStats(),
    fetchRecruiterCount(),
    fetchAssignmentStats(),
    fetchDashboardStats(),
  ]);

  const val = (r) => (r.status === "fulfilled" ? r.value : null);
  const t = val(teachers);
  const l = val(leaves);
  const r = val(recruiters);
  const a = val(assignments);
  const d = val(dashboard);

  const labels = language === "en"
    ? {
        teachers: "Teachers",
        pending: "Pending leave",
        recruiters: "Recruiters",
        assignments: "Assignments",
        licensed: "Licensed",
      }
    : {
        teachers: "Enseignantes",
        pending: "Congés en attente",
        recruiters: "Recruteurs",
        assignments: "Affectations",
        licensed: "Licenciés",
      };

  return [
    { id: "teachers", label: labels.teachers, value: t?.totalTeachers ?? "—", route: "/enseignantes" },
    { id: "pending", label: labels.pending, value: l?.stats?.pending ?? "—", route: "/leave-requests", highlight: (l?.stats?.pending ?? 0) > 0 },
    { id: "recruiters", label: labels.recruiters, value: r?.count ?? "—", route: "/recruiters" },
    { id: "assignments", label: labels.assignments, value: a?.totalRecords ?? "—", route: "/affectations" },
    {
      id: "licensed",
      label: labels.licensed,
      value:
        d?.maxLicensedTeachers > 0
          ? `${d.licensedTeachers}/${d.maxLicensedTeachers}`
          : d?.licensedTeachers ?? "—",
      route: "/teacher-license",
    },
  ].filter((c) => c.value !== "—" || c.id === "pending");
}
